import styled from "styled-components";
import { useEffect, useState } from "react";
import { X, Check } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";
import { apiFetch } from "./api";

// Change / Swap room for a still-reserved line — a faithful port of the /admin room-booking-line
// modals (same endpoints + services): Change shows the available same-type rooms as a building/floor
// grid; Swap lists other same-date bookings and free-to-take blocked rooms. Pick one, then an in-app
// confirmation before the move is committed. `mode` is "change" or "swap".
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(26, 26, 26, 0.5);
  backdrop-filter: blur(2px);
`;

const Panel = styled.div`
  width: 100%;
  max-width: 640px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 14px;
  border-top: 3px solid ${theme.gold};
  box-shadow: 0 24px 60px -18px rgba(26, 26, 26, 0.5);
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.9rem 1rem 0.6rem;
  border-bottom: 1px solid ${theme.border};
`;

const Title = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.85rem 1.1rem;
`;

const Intro = styled.p`
  margin: 0 0 0.7rem;
  font-size: 0.84rem;
  line-height: 1.5;
  color: ${theme.ink700};
`;

const Muted = styled.div`
  padding: 0.6rem 0.25rem;
  font-size: 0.9rem;
  color: ${theme.muted};
`;

const ErrorNote = styled.div`
  margin-bottom: 0.7rem;
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  background: #fdecec;
  border: 1px solid #f2c4c0;
  color: #b91c1c;
  font-size: 0.85rem;
  font-weight: 600;
`;

const BuildingBlock = styled.div`
  margin-bottom: 1rem;
`;

const BuildingName = styled.div`
  margin-bottom: 0.3rem;
  font-size: 0.86rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const FloorGroup = styled.div`
  & + & {
    margin-top: 0.55rem;
  }
`;

const FloorHeading = styled.div`
  padding: 0.25rem 0.1rem;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${theme.muted};
`;

const RoomList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  overflow: hidden;
`;

// A ROW, not a cell. Each room now carries a whole sentence ("tukar dengan: Kadek Sierly Saraswati",
// "Tanggal menginap kedua booking harus sama"), and in a wrapping grid every tile sized itself to its
// own text - ragged rows, mismatched widths, gaps. One full-width row per room lets the text run as
// long as it needs while the room codes stay in a readable column.
const RoomRow = styled.button`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
  padding: 0.5rem 0.7rem;
  border: 0;
  border-top: 1px solid ${theme.border};
  background: ${(props) =>
    props.$selected ? "#e8f5e9" : props.$current ? "#eef5fd" : "#ffffff"};
  box-shadow: ${(props) =>
    props.$selected ? "inset 3px 0 0 #4caf50" : "none"};
  cursor: ${(props) => (props.$selectable ? "pointer" : "default")};
  text-align: left;
  &:first-child {
    border-top: 0;
  }
  &:hover {
    background: ${(props) =>
      props.$selectable && !props.$selected ? "#f6faf6" : undefined};
  }
`;

const RoomCode = styled.div`
  flex: 0 0 3.1rem;
  font-size: 0.95rem;
  font-weight: 800;
  color: ${(props) => (props.$dim ? theme.muted : theme.ink)};
`;

// min-width: 0 is what actually lets a long guest name wrap instead of pushing the row wider.
const RoomInfo = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const RoomNote = styled.div`
  font-size: 0.8rem;
  line-height: 1.35;
  color: ${(props) => props.$color || theme.muted};
`;

const ModeTag = styled.span`
  flex: 0 0 auto;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  background: ${(props) => props.$bg};
  color: ${(props) => props.$fg};
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.02em;
`;

const TickSlot = styled.span`
  flex: 0 0 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #2e7d32;
`;

const ConfirmBox = styled.div`
  padding: 0.7rem 0.85rem;
  border-radius: 8px;
  background: ${theme.goldSoft};
  border-left: 4px solid ${theme.goldDark};
  font-size: 0.92rem;
  line-height: 1.5;
  color: ${theme.ink};
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 1.1rem;
  border-top: 1px solid ${theme.border};
`;

const Selected = styled.span`
  margin-right: auto;
  font-size: 0.84rem;
  color: ${theme.muted};
`;

const Cancel = styled.button`
  padding: 0.45rem 1rem;
  border: 1px solid ${theme.border};
  border-radius: 9px;
  background: #ffffff;
  font-size: 0.9rem;
  font-weight: 700;
  color: ${theme.ink700};
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const Confirm = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 1.1rem;
  border: 0;
  border-radius: 9px;
  background: ${theme.ink};
  color: ${theme.gold};
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${theme.inkSoft};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// One dialog. The operator picks a room; the server has already worked out what would happen in it
// (move into an empty one, swap with the guest already there, take over a blocked one) and says so on
// the cell. Splitting this into "change room" and "swap room" made the operator guess which of the
// two would work before they could see either list.
const RoomMoveModal = (props) => {
  const { t } = useT();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const base = `/frontdesk_v2/bookings/${props.bookingId}`;

  useEffect(() => {
    let active = true;
    const url = `${base}/move_targets?change_line_id=${props.changeLineId}`;
    const run = async () => {
      setLoading(true);
      setError("");
      setPayload(null);
      setSelected(null);
      setConfirming(false);
      setAcknowledged(false);
      try {
        const res = await apiFetch(url);
        if (!active) {
          return;
        }
        if (res && res.data) {
          setPayload(res.data);
        } else {
          setError(res?.message || t("error_body"));
        }
      } catch {
        if (active) {
          setError(t("error_body"));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [base, props.changeLineId, t]);

  const submit = async () => {
    setProcessing(true);
    setError("");
    try {
      const res = await apiFetch(`${base}/move_room`, {
        method: "POST",
        body: JSON.stringify({
          change_line_id: props.changeLineId,
          target_room_id: selected?.roomId,
          acknowledge_conflicts: acknowledged,
          line_id: props.anchorLineId,
        }),
      });
      if (res?.data?.booking) {
        props.onChanged(res.data, res.message);
        props.onClose();
      } else {
        setError(res?.message || t("error_body"));
        setConfirming(false);
      }
    } catch {
      setError(t("error_body"));
      setConfirming(false);
    } finally {
      setProcessing(false);
    }
  };

  const modeLabel = (room) => {
    if (room.mode === "SWAP") {
      return `${t("move_room_mode_swap")}: ${room.occupant?.guest || t("guest")}`;
    }
    if (room.mode === "BLOCK_CONVERT") {
      return t("move_room_mode_block");
    }
    return t("move_room_mode_free");
  };

  // Acknowledgement is required by the server, not merely offered here: moving a day-use stay into a
  // room an overnight guest holds is the same decision as selling one into it.
  const needsAck = (selected?.transitOverlaps || []).length > 0;

  const confirmMessage = `${t("confirm_change_room")} ${selected?.code || ""}${
    selected?.mode === "SWAP"
      ? ` ⇄ ${selected?.occupantGuest || t("guest")}`
      : ""
  }`;

  const MODE_TAGS = {
    MOVE: { label: t("move_room_tag_move"), bg: "#e8f5e9", fg: "#1b5e20" },
    SWAP: { label: t("move_room_tag_swap"), bg: "#fff4e5", fg: "#8a4b00" },
    BLOCK_CONVERT: {
      label: t("move_room_tag_block"),
      bg: "#eef2ff",
      fg: "#3730a3",
    },
  };

  const renderRoom = (room) => {
    const selectable = room.eligible && !room.current;
    const isSelected = selected?.roomId === room.id;
    const tag = room.eligible && !room.current ? MODE_TAGS[room.mode] : null;

    return (
      <RoomRow
        key={room.id}
        type="button"
        $selectable={selectable}
        $selected={isSelected}
        $current={room.current}
        disabled={!selectable}
        onClick={() =>
          setSelected({
            roomId: room.id,
            code: room.code,
            mode: room.mode,
            occupantGuest: room.occupant?.guest,
            transitOverlaps: room.transit_overlaps || [],
          })
        }
      >
        <RoomCode $dim={!room.eligible && !room.current}>{room.code}</RoomCode>
        <RoomInfo>
          {room.current ? (
            <RoomNote $color="#1d5fa8">{t("change_room_current")}</RoomNote>
          ) : !room.eligible ? (
            <RoomNote>{room.reason || t("change_room_unavailable")}</RoomNote>
          ) : (
            <RoomNote $color="#15803d">{modeLabel(room)}</RoomNote>
          )}
          {(room.transit_overlaps || []).length > 0 ? (
            <RoomNote $color="#0284c7">{t("move_room_transit_note")}</RoomNote>
          ) : null}
        </RoomInfo>
        {tag ? (
          <ModeTag $bg={tag.bg} $fg={tag.fg}>
            {tag.label}
          </ModeTag>
        ) : null}
        <TickSlot>{isSelected ? <Check size={14} /> : null}</TickSlot>
      </RoomRow>
    );
  };

  const renderList = () => {
    const buildings = payload?.buildings || [];
    if (buildings.length === 0) {
      return <Muted>{t("change_room_none")}</Muted>;
    }
    return buildings.map((building) => (
      <BuildingBlock key={building.id || building.name}>
        <BuildingName>{building.name || "—"}</BuildingName>
        {building.floors.map((floor) => (
          <FloorGroup key={floor.id || floor.code}>
            <FloorHeading>
              {t("move_room_floor")} {floor.code || floor.name || ""}
            </FloorHeading>
            <RoomList>{floor.rooms.map(renderRoom)}</RoomList>
          </FloorGroup>
        ))}
      </BuildingBlock>
    ));
  };

  return (
    <Overlay onClick={props.onClose}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <Head>
          <Title>
            {t("move_room_title")}
            {props.currentCode ? ` — ${props.currentCode}` : ""}
          </Title>
          <IconButton
            type="button"
            aria-label={t("close")}
            onClick={props.onClose}
          >
            <X size={16} />
          </IconButton>
        </Head>
        <Body>
          <Intro>{t("move_room_intro")}</Intro>
          {error ? <ErrorNote>{error}</ErrorNote> : null}
          {loading ? (
            <Muted>{t("loading")}</Muted>
          ) : confirming ? (
            <ConfirmBox>{confirmMessage}</ConfirmBox>
          ) : (
            renderList()
          )}
          {!loading && !confirming && needsAck ? (
            <ErrorNote>
              <label>
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                />{" "}
                {selected.transitOverlaps[0]?.message_id ||
                  t("move_room_ack_label")}
              </label>
            </ErrorNote>
          ) : null}
        </Body>
        <Foot>
          <Selected>
            {selected
              ? `${t("change_room_selected")}: ${selected.code || ""}`
              : ""}
          </Selected>
          <Cancel
            type="button"
            onClick={() =>
              confirming ? setConfirming(false) : props.onClose()
            }
          >
            {t("confirm_cancel")}
          </Cancel>
          <Confirm
            type="button"
            disabled={!selected || processing || (needsAck && !acknowledged)}
            onClick={() => (confirming ? submit() : setConfirming(true))}
          >
            <Check size={15} />
            {confirming ? t("move_room_ok") : t("change_room_confirm")}
          </Confirm>
        </Foot>
      </Panel>
    </Overlay>
  );
};

export default RoomMoveModal;
